import type { SlackWorkspaceConnection, TriageItem } from '../../types/models';
import { CheckIcon, CloseIcon, CursorSpark, MailIcon, PaperclipIcon } from '../Icon';
import btn from '../../styles/buttons.module.css';
import styles from './Triage.module.css';

interface TriageProps {
  items: TriageItem[];
  onAssign: (itemId: string) => void;
  onCursor: (title: string) => void;
  /** Dismiss an item that shouldn't be in triage. */
  onDismiss: (itemId: string) => void;
  /** Id of the item currently being routed by Claude (shows an inline spinner). */
  pendingId?: string | null;
  /** Ask Claude to scan connected inboxes for actionable messages. */
  onScan?: () => void;
  /** True while a scan is running. */
  scanning?: boolean;
  /** True once Gmail is connected (a refresh token is stored server-side). */
  gmailConnected?: boolean;
  /** Run the one-time "Connect Gmail" consent flow. */
  onConnectGmail?: () => void;
  /** True while the connect flow is running. */
  connectingGmail?: boolean;
  /** Forget the stored Gmail connection. */
  onDisconnectGmail?: () => void;
  /** Connected Slack workspaces (zero or more). */
  slackConnections?: SlackWorkspaceConnection[];
  /** Run the "connect a Slack workspace" popup flow. */
  onConnectSlack?: () => void;
  /** True while a Slack connect flow is running. */
  connectingSlack?: boolean;
  /** Forget one connected Slack workspace. */
  onDisconnectSlack?: (teamId: string) => void;
  /** Tag (or clear) a connected workspace's associated CRM client. */
  onSetSlackWorkspaceClient?: (teamId: string, clientId: string | null) => void;
  /** Client roster, for the workspace-to-client tag dropdown. */
  clients?: Array<{ id: string; name: string }>;
}

export function Triage({
  items,
  onAssign,
  onCursor,
  onDismiss,
  pendingId,
  onScan,
  scanning,
  gmailConnected,
  onConnectGmail,
  connectingGmail,
  onDisconnectGmail,
  slackConnections,
  onConnectSlack,
  connectingSlack,
  onDisconnectSlack,
  onSetSlackWorkspaceClient,
  clients,
}: TriageProps) {
  return (
    <section className={styles.section}>
      <div className={styles.head}>
        <div className={styles.headLeft}>
          <span className={styles.heading}>Triage</span>
          <span className={styles.count}>{items.length} TO SORT</span>
        </div>
        {onScan ? (
          <div className={styles.scanGroup}>
            {gmailConnected ? (
              <>
                <button
                  type="button"
                  className={styles.connected}
                  onClick={onDisconnectGmail}
                  title="Disconnect Gmail"
                >
                  <CheckIcon size={11} />
                  Gmail connected
                </button>
                <button
                  type="button"
                  className={styles.scan}
                  onClick={onScan}
                  disabled={scanning}
                >
                  {scanning ? (
                    <>
                      <span className={styles.spinner} />
                      Scanning…
                    </>
                  ) : (
                    <>
                      <MailIcon size={13} />
                      Scan inboxes
                    </>
                  )}
                </button>
              </>
            ) : (
              <button
                type="button"
                className={styles.scan}
                onClick={onConnectGmail}
                disabled={connectingGmail}
              >
                {connectingGmail ? (
                  <>
                    <span className={styles.spinner} />
                    Connecting…
                  </>
                ) : (
                  <>
                    <MailIcon size={13} />
                    Connect Gmail
                  </>
                )}
              </button>
            )}
          </div>
        ) : (
          <span className={styles.hint}>
            One inbox — assign to a client or hand to an agent
          </span>
        )}
      </div>

      {onScan && (
        <div className={styles.integrations}>
          <span className={styles.integrationsLabel}>Slack</span>
          {slackConnections?.map((conn) => (
            <div key={conn.teamId} className={styles.slackChip}>
              <span className={styles.slackDot} />
              <span className={styles.slackName}>{conn.teamName}</span>
              {conn.needsReauth && (
                <span className={styles.slackWarn} title="Connection expired — reconnect it">
                  !
                </span>
              )}
              <select
                className={styles.slackClientSelect}
                value={conn.clientId ?? ''}
                onChange={(e) =>
                  onSetSlackWorkspaceClient?.(conn.teamId, e.target.value || null)
                }
                title="Pre-assign triage items from this workspace to a client"
              >
                <option value="">General / unassigned</option>
                {clients?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className={styles.slackDisconnect}
                onClick={() => onDisconnectSlack?.(conn.teamId)}
                aria-label="Disconnect workspace"
                title="Disconnect workspace"
              >
                <CloseIcon size={11} />
              </button>
            </div>
          ))}
          <button
            type="button"
            className={styles.slackConnectBtn}
            onClick={onConnectSlack}
            disabled={connectingSlack}
          >
            {connectingSlack ? (
              <>
                <span className={styles.spinner} />
                Connecting…
              </>
            ) : (
              '+ Connect a Slack workspace'
            )}
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <div className={styles.empty}>✓ Inbox zero — nothing to triage</div>
      ) : (
        <div className={`${styles.rail} sb-scroll`}>
          {items.map((chip) => (
            <div key={chip.id} className={styles.chip}>
              <div className={styles.chipTop}>
                <span
                  className={styles.dot}
                  style={{ background: chip.sourceColor }}
                />
                <span className={styles.source}>{chip.source}</span>
                {Boolean(chip.attachments?.length) && (
                  <span className={styles.attachmentCount} title={`${chip.attachments!.length} attachment(s)`}>
                    <PaperclipIcon size={11} />
                    {chip.attachments!.length}
                  </span>
                )}
                <span className={styles.age}>{chip.age}</span>
                <button
                  type="button"
                  className={styles.dismiss}
                  onClick={() => onDismiss(chip.id)}
                  aria-label="Dismiss"
                  title="Dismiss — remove from triage"
                >
                  <CloseIcon size={13} />
                </button>
              </div>
              <div className={styles.chipTitle}>{chip.title}</div>
              {chip.suggestedClientName && (
                <div className={styles.suggested} title="Matched by sender domain">
                  → {chip.suggestedClientName}
                </div>
              )}
              {pendingId === chip.id ? (
                <div className={styles.assigning}>
                  <span className={styles.spinner} />
                  Assigning…
                </div>
              ) : (
                <div className={styles.actions}>
                  <button
                    type="button"
                    className={`${btn.btn} ${btn.ghost} ${btn.grow}`}
                    onClick={() => onAssign(chip.id)}
                  >
                    Assign →
                  </button>
                  <button
                    type="button"
                    className={`${btn.btn} ${btn.cta}`}
                    onClick={() => onCursor(chip.title)}
                  >
                    <CursorSpark size={12} />
                    Cursor
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
